import { useState } from 'react';
import { claveIdempotencia } from '../../../api/comun';
import { Desplegable } from '../../../componentes/Desplegable';
import { Fecha } from '../../../componentes/Fecha';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { dinero } from '../../../logica/dinero';
import { hoyLocal } from '../../../logica/fechasCortas';
import { Bloque, Caja, Vacia } from '../comun';
import { codigoDe, useAvisarError, useCarga } from '../uso';
import { diaCorto, nombreRrpp, opsDe, periodosRapidos } from '../textos';
import type { ContextoLiquidaciones } from './ApartadoLiquidaciones';

/**
 * Preparar una liquidación (10.11, `createSettlement`): RRPP y periodo (fechas locales de los eventos). La vista previa son
 * sus comisiones pendientes del periodo (`listCommissions`); las provisionales (noche sin terminar) no entran. Un
 * borrador por RRPP: con uno abierto, se abre ese.
 */

export function Preparar({ ctx, rrppInicial }: { ctx: ContextoLiquidaciones; rrppInicial: string }) {
    const { props, rrpp, borradores } = ctx;
    const { negocio, movil } = props;
    const ops = opsDe(props.ops);
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const rapidos = periodosRapidos();
    const [quien, setQuien] = useState(rrppInicial);
    const [desde, setDesde] = useState(rapidos[2]?.desde ?? '');
    const [hasta, setHasta] = useState(rapidos[2]?.hasta ?? '');
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [clave] = useState(claveIdempotencia);
    const borrador = borradores.find((b) => b.promoter_id === quien);
    const previa = useCarga(quien !== '' && desde !== '' && hasta !== '' && desde <= hasta ? () => ops.listCommissions(negocio, { promoter_id: quien, status: 'pending', from: desde, to: hasta, limit: 100 }) : null, [negocio, quien, desde, hasta]);
    const comisiones = previa.datos?.data ?? [];
    const noches = new Set(comisiones.map((c) => c.event_id)).size;
    const error = !probado
        ? null
        : quien === ''
          ? 'Elige el RRPP.'
          : borrador !== undefined
            ? `${nombreRrpp(rrpp.get(quien))} ya tiene un borrador: ábrelo o descártalo.`
            : desde > hasta
              ? 'La fecha de inicio va antes que la de fin.'
              : comisiones.length === 0
                ? 'No hay comisiones pendientes en ese periodo.'
                : null;

    const preparar = () => {
        if (quien === '' || borrador !== undefined || desde > hasta || comisiones.length === 0) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        ops.createSettlement(negocio, { promoter_id: quien, period_from: desde, period_to: hasta }, clave)
            .then((l) => {
                avisar({ tipo: 'ok', texto: 'Borrador preparado. Revísalo y ciérralo cuando esté bien.' });
                ctx.recargar();
                ctx.abrir({ tipo: 'ficha', id: l.id });
            })
            .catch((e: unknown) => {
                avisarError(e, codigoDe(e) === 'conflict' ? 'Ese RRPP ya tiene un borrador: ábrelo o descártalo.' : undefined);
            })
            .finally(() => { setOcupado(false); });
    };

    return (
        <PanelLateral
            abierto
            onCerrar={() => { ctx.abrir(null); }}
            movil={movil}
            titulo="Preparar liquidación"
            eyebrow="Liquidaciones"
            subtitulo="Elige RRPP y periodo; luego la revisas."
            icono="wallet"
            accion={borrador === undefined ? 'Preparar' : 'Abrir su borrador'}
            accionIcono="file-text"
            ocupado={ocupado}
            ocupadoTexto="Preparando…"
            onAccion={borrador === undefined ? preparar : () => { ctx.abrir({ tipo: 'ficha', id: borrador.id }); }}
        >
            <div className="rv-ca-cuerpo">
                <Bloque>
                    <Desplegable
                        etiqueta="RRPP"
                        valor={quien}
                        onChange={(v) => { setQuien(v); setProbado(false); }}
                        error={probado && (quien === '' || borrador !== undefined) ? error : null}
                        opciones={[
                            { valor: '', texto: 'Elige un RRPP' },
                            ...[...rrpp.values()].map((p) => ({ valor: p.id, texto: `${nombreRrpp(p)} · ${borradores.some((b) => b.promoter_id === p.id) ? 'tiene un borrador' : (p.pending_total?.amount ?? 0) > 0 ? `${dinero(p.pending_total)} pendiente` : 'nada pendiente'}` })),
                        ]}
                    />
                </Bloque>
                <Bloque titulo="Periodo">
                    <div className={`rv-ls-opciones${movil ? ' rv-ls-opciones--movil' : ''}`} role="radiogroup" aria-label="Periodo">
                        {rapidos.map((r) => (
                            <OpcionRadio key={r.texto} elegida={desde === r.desde && hasta === r.hasta} onElegir={() => { setDesde(r.desde); setHasta(r.hasta); }} titulo={r.texto} texto={`${diaCorto(r.desde)} – ${diaCorto(r.hasta)}`} icono="calendar-days" />
                        ))}
                    </div>
                    <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`}>
                        <Fecha etiqueta="Desde" valor={desde} onChange={setDesde} max={hoyLocal()} />
                        <Fecha etiqueta="Hasta" valor={hasta} onChange={setHasta} max={hoyLocal()} error={probado && desde > hasta ? error : null} />
                    </div>
                </Bloque>
                {quien !== '' && borrador === undefined && (
                    <Bloque titulo="Lo que entra">
                        {previa.datos === null ? (
                            <p className="rv-ca-nota rv-ca-nota--gris">Calculando…</p>
                        ) : comisiones.length === 0 ? (
                            <Vacia icono="wallet" titulo="Nada pendiente en ese periodo" texto="Prueba con otro periodo." />
                        ) : (
                            <>
                                <div className="rv-ls-cifras-tres">
                                    <div className="rv-ls-cifra"><strong>Comisiones</strong><span><b>{comisiones.length}</b></span><small>de {noches} {noches === 1 ? 'noche' : 'noches'}</small></div>
                                </div>
                                <Caja icono="hourglass">Las comisiones de una noche que no ha terminado son provisionales y no entran. El importe lo calcula el borrador.</Caja>
                            </>
                        )}
                        {probado && error !== null && comisiones.length === 0 && <Caja icono="circle-alert" tono="error">{error}</Caja>}
                    </Bloque>
                )}
                {borrador !== undefined && <Caja icono="file-text">{nombreRrpp(rrpp.get(quien))} ya tiene un borrador: ábrelo o descártalo.</Caja>}
            </div>
        </PanelLateral>
    );
}
